import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search, Globe, Zap, User, Crown, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useDataSaver } from "@/context/DataSaverContext";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/candidates", label: "Candidates" },
  { to: "/delegations", label: "Delegations" },
  { to: "/watch", label: "Watch" },
  { to: "/events", label: "Events" },
  { to: "/coronation", label: "Coronation" },
  { to: "/predictions", label: "Predictions" },
  { to: "/impact", label: "Impact" },
  { to: "/news", label: "News" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/shop", label: "Shop" },
  { to: "/apply", label: "Apply" },
  { to: "/support", label: "Support" },
];

const LANGS = ["EN", "FR", "AR", "PT", "ES", "SW"];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang] = useState("EN");
  const { user } = useAuth();
  const { dataSaver, toggle } = useDataSaver();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 glass-strong border-b border-gold/20" data-testid="site-header">
      <div className="section flex h-16 items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <Crown className="h-6 w-6 text-gold" />
          <span className="hidden sm:block leading-none">
            <span className="block font-serif text-sm text-amber-50 tracking-wide">MISS UNITED STATES</span>
            <span className="block font-mono text-[0.55rem] tracking-[0.3em] gold-text">OF AFRICA</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-2.5 py-2 text-xs font-display uppercase tracking-wider transition-colors ${
                  isActive ? "text-gold" : "text-amber-50/70 hover:text-gold"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <button
            onClick={toggle}
            title="Data Saver Mode"
            data-testid="data-saver-toggle"
            className={`hidden sm:flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-[0.6rem] font-mono uppercase tracking-wider transition-all ${
              dataSaver ? "border-emerald-accent text-emerald-accent" : "border-gold/25 text-amber-50/60"
            }`}
          >
            <Zap className="h-3 w-3" /> {dataSaver ? "Saver On" : "Saver"}
          </button>

          <div className="relative hidden sm:block">
            <button
              onClick={() => setLangOpen((v) => !v)}
              data-testid="lang-toggle"
              className="flex items-center gap-1 rounded-full border border-gold/25 px-2.5 py-1.5 text-[0.6rem] font-mono text-amber-50/70 hover:border-gold/60"
            >
              <Globe className="h-3 w-3" /> {lang} <ChevronDown className="h-3 w-3" />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-24 rounded-md glass-strong p-1" onMouseLeave={() => setLangOpen(false)}>
                {LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => { setLang(l); setLangOpen(false); }}
                    data-testid={`lang-${l}`}
                    className="block w-full rounded px-3 py-1.5 text-left text-xs text-amber-50/80 hover:bg-gold/10"
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => navigate("/candidates")} data-testid="search-btn" className="p-2 text-amber-50/70 hover:text-gold">
            <Search className="h-4 w-4" />
          </button>

          <Link to={user ? "/account" : "/login"} data-testid="account-link" className="p-2 text-amber-50/70 hover:text-gold">
            <User className="h-4 w-4" />
          </Link>

          <Link to="/vote" data-testid="vote-now-header" className="btn-gold hidden sm:inline-flex px-5 py-2 text-xs animate-pulseGold">
            Vote Now
          </Link>

          <button onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn" className="xl:hidden p-2 text-gold">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-gold/15 glass-strong" data-testid="mobile-menu">
          <nav className="section grid grid-cols-2 gap-1 py-4">
            {[{ to: "/", label: "Home" }, ...NAV, { to: "/vote", label: "Vote" }].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded px-3 py-2.5 text-sm font-display uppercase tracking-wide text-amber-50/80 hover:bg-gold/10"
              >
                {n.label}
              </NavLink>
            ))}
            <button
              onClick={() => { toggle(); }}
              className="col-span-2 mt-1 rounded border border-gold/25 px-3 py-2.5 text-left text-sm text-amber-50/80"
            >
              Data Saver: {dataSaver ? "ON" : "OFF"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
